import { activeSessionIdAtom } from '@heidi/session/data';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { magicFlowPendingDeepLinkAtom } from './magic-flow-pending-deep-link';

export const MAGIC_FLOW_DEEP_LINK_LANDING_PATH = '/scribe/my-templates';

export type MagicFlowLinkOnboardingFacts = {
  onboardingPendingAt: string | null | undefined;
  hasCompletedOnboarding: boolean;
  /** Flags have resolved whether the in-app onboarding redirect can run. */
  isResumeDecided: boolean;
  /** The in-app onboarding redirect is enabled. */
  canResume: boolean;
};

/**
 * Mirrors the pending-onboarding redirect: hold the link while that redirect can
 * still run, and never for a leftover mark it will not act on.
 */
export const isMagicFlowLinkHeldForOnboarding = ({
  onboardingPendingAt,
  hasCompletedOnboarding,
  isResumeDecided,
  canResume,
}: MagicFlowLinkOnboardingFacts): boolean =>
  Boolean(onboardingPendingAt) &&
  !hasCompletedOnboarding &&
  (!isResumeDecided || canResume);

export type UseRouteMagicFlowDeepLinkArgs = {
  isSignedIn: boolean;
  /** In-app onboarding is unfinished; the pending-onboarding guard owns routing until it ends. */
  isOnboardingPending: boolean;
};

/**
 * Routes a signed-in user with a pending Magic Flow link to My Templates, which
 * opens it like "Start set up". Waits out unfinished onboarding and recordings.
 */
export const useRouteMagicFlowDeepLink = ({
  isSignedIn,
  isOnboardingPending,
}: UseRouteMagicFlowDeepLinkArgs) => {
  const pendingLink = useAtomValue(magicFlowPendingDeepLinkAtom);
  const recordingSessionId = useAtomValue(activeSessionIdAtom);
  const router = useRouter();
  // The router object changes every render, so push once per link.
  const routedTemplateIdRef = useRef<string | null>(null);

  const pendingTemplateId = pendingLink?.templateId ?? null;
  const canRoute =
    pendingTemplateId != null &&
    isSignedIn &&
    !isOnboardingPending &&
    recordingSessionId == null &&
    router.isReady;

  useEffect(() => {
    if (pendingTemplateId == null) {
      routedTemplateIdRef.current = null;
      return;
    }
    if (!canRoute || routedTemplateIdRef.current === pendingTemplateId) return;
    if (router.pathname === MAGIC_FLOW_DEEP_LINK_LANDING_PATH) return;

    routedTemplateIdRef.current = pendingTemplateId;
    // A cancelled navigation frees the link to route again.
    void router
      .push(MAGIC_FLOW_DEEP_LINK_LANDING_PATH)
      .then((navigated) => {
        if (!navigated) routedTemplateIdRef.current = null;
      })
      .catch(() => {
        routedTemplateIdRef.current = null;
      });
  }, [pendingTemplateId, canRoute, router]);
};
